import { apiClient } from '@/api/apiClient'
import { GoodsFamily, ReportingPeriodBasis } from '@/common'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type Installation = {
  id: string
  name: string
  scustomId?: string
  goodsFamily: GoodsFamily
  basisForReportingPeriod: ReportingPeriodBasis
  startMonth: number
  monthInterval: number
  partnershipId: string
  street: string
  streetNumber: string
  street2: string
  postCode: string
  city: string
  poBox: string
  country: string
  unlocode: string
  longitude: number
  latitude: number
}

export type CreateInstallationPayload = {
  name: string
  customId?: string
  goodsFamily: GoodsFamily
  basisForReportingPeriod: ReportingPeriodBasis
  startMonth: number
  monthInterval: number
  street?: string
  streetNumber?: string
  street2?: string
  postCode?: string
  city: string
  poBox?: string
  country: string
  unlocode?: string
  longitude: number
  latitude: number
}

type UpdateInstallationPayload = CreateInstallationPayload

export const queryKeyInstallations = 'installations'

export const useGetAllInstallationsForOrganization = (
  organizationId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [queryKeyInstallations, organizationId],
    queryFn: async () => {
      const response = await apiClient.get<Installation[]>(
        `/v2/organizations/${organizationId}/installations`
      )
      return response.data
    },
    enabled: options?.enabled ?? true
  })
}

export const useGetAllInstallationsForPartnership = (
  partnershipId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [queryKeyInstallations, partnershipId],
    queryFn: async () => {
      const response = await apiClient.get<Installation[]>(
        `/v2/partnerships/${partnershipId}/installations`
      )
      return response.data
    },
    enabled: options?.enabled ?? true
  })
}

export const useCreateInstallation = (partnershipId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyInstallations, partnershipId],
    mutationFn: async (payload: CreateInstallationPayload) => {
      const response = await apiClient.post<Installation>(
        `/v2/partnerships/${partnershipId}/installations`,
        payload
      )

      queryClient.setQueryData<Installation[]>([queryKeyInstallations, partnershipId], oldData => [
        ...(oldData || []),
        response.data
      ])

      return response.data
    }
  })
}

export const useUpdateInstallation = (partnershipId: string, installationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyInstallations, partnershipId, installationId],
    mutationFn: async (payload: UpdateInstallationPayload) => {
      const response = await apiClient.put<Installation>(
        `/v2/partnerships/${partnershipId}/installations/${installationId}`,
        payload
      )

      queryClient.setQueryData<Installation[]>(
        [queryKeyInstallations, partnershipId],
        oldData =>
          oldData?.map(installation =>
            installation.id === response.data.id ? response.data : installation
          ) || [response.data]
      )

      return response.data
    }
  })
}

export const useDeleteInstallation = (partnershipId: string, installationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(
        `/v2/partnerships/${partnershipId}/installations/${installationId}`
      )

      queryClient.setQueryData<Installation[]>(
        [queryKeyInstallations, partnershipId],
        oldData => oldData?.filter(installation => installation.id !== installationId) || []
      )
    }
  })
}
